import React, { useState, useMemo } from 'react';
import { withRouter } from 'react-router-dom';
import { compose } from 'recompose';
import { connect } from 'react-redux';

import { EXAM_MODE, QUIZ_VARIANT } from 'Util/constants';
import { navigateToTungKai } from 'Util/navigateToTungKai';
import { canRedirectToTungKai } from 'Routes/MyCourses/Components/utils.js';
import { useConfig } from 'Context/ConfigContext';
import { useClassroomNavigation } from 'Util/Hooks';

import { myProfile } from '../../../../Redux/user-profile/selectors';
import useSubsectionContent from '../../../../Routes/Classroom/Domain/useSubsectionContent';
import { ClassroomQuizSession } from '../../../../Domain/ClassroomQuiz';
import { useQuizContext } from '../../../../Context/QuizContext';
import { QUIZ_SUB_ROUTES } from '../../constant';
import UpcomingExerciseModal from '../../Components/UpcomingExerciseModal';
import { useClassroomContext } from '../../../../Context/ClassroomContext';
import { withCapacitor } from '../../../../Util/Capacitors/hoc';

const Landing = ({ history, match, children, capacitorContext, user }) => {
  const {
    metadata: { title, maxAttempts, timeLimit, fullScore, passingScore, resultOptions },
    variant,
    openCourseReviewModal,
    isLastSubsectionInCourse,
    onExitClassroom,
  } = useQuizContext();
  const { tungKai, userApi } = useConfig();
  const { openInApp } = capacitorContext;

  const { examId } = match.params;

  const handleSeeReport = (reportId, roundIndex) => {
    history.push(`${QUIZ_SUB_ROUTES.SOLUTION.getPath(examId, reportId, roundIndex)}`);
  };

  const [isUpcomingOpen, setIsUpcomingOpen] = useState(false);
  const { classroomCourseId, courseId, currentSubsectionId, currentSubsectionReferId } =
    useClassroomContext();
  const { currentSubsection } = useClassroomNavigation();

  const { data: subsectionContentData } = useSubsectionContent({
    variables: {
      classroomCourseId,
      subSectionId: currentSubsectionId,
      subSectionReferId: currentSubsectionReferId,
    },
    fetchPolicy: 'cache-only',
  });

  const examMode = useMemo(
    () =>
      subsectionContentData?.classroomSubSectionContent?.assets?.contentAssets?.[0]?.content
        ?.examMode,
    [subsectionContentData]
  );
  const isQuizFeed = currentSubsection?.contentType === 'QUIZ_FEED';

  const { enable, enableWhiteList, whitelist, quizFooterRedirectPath } = tungKai || {};

  const canGoToTungKai = canRedirectToTungKai({
    enable,
    enableWhiteList,
    whitelist,
    userId: user?.id,
  });

  return (
    <ClassroomQuizSession>
      {({ quizSessions, startSession, examSessionId, isLoadingQuizSession }) => {
        if (examSessionId) {
          const path = QUIZ_SUB_ROUTES.SESSION.getPath(examId, examSessionId);
          history.push(path);
        }

        return (
          <>
            {children({
              reports: quizSessions,
              title,
              remainingRound: maxAttempts - quizSessions.length,
              totalRound: maxAttempts,
              timeLimit,
              fullScore,
              passingScore,
              resultOptions,
              variant,
              openCourseReviewModal,
              isLastSubsectionInCourse,
              onStartQuiz: async () => {
                if ((examMode === EXAM_MODE.RANDOM || isQuizFeed) && canGoToTungKai) {
                  const variant = isQuizFeed ? QUIZ_VARIANT.QUIZ_FEED : QUIZ_VARIANT.QUIZ_V2;
                  const redirectPath = (quizFooterRedirectPath || '/')
                    .replace('{courseId}', courseId)
                    .replace('{subsectionId}', currentSubsectionId)
                    .replace('{variant}', variant);

                  return navigateToTungKai({
                    redirectPath,
                    config: { userApi, tungKai },
                    openInApp,
                  });
                }

                await startSession();
              },
              onSeeReport: handleSeeReport,
              isQuizStarting: isLoadingQuizSession,
              onExitClassroom: onExitClassroom,
            })}
            <UpcomingExerciseModal
              open={isUpcomingOpen}
              onAcknowledge={async () => {
                setIsUpcomingOpen(false);
              }}
              onClose={() => setIsUpcomingOpen(false)}
            />
          </>
        );
      }}
    </ClassroomQuizSession>
  );
};

export default compose(
  withRouter,
  withCapacitor,
  connect((state) => ({
    user: myProfile(state) ?? {},
  }))
)(Landing);
